import type { ResolvedPersona } from "./types";
import { BLOG_ENABLED } from "../content/blogPosts/enabled";

/**
 * The single source of truth for persona-relevant destinations. The homepage spotlight,
 * the site-wide "where to next" band, and the adaptive header CTA all read from here, so
 * tuning one place tunes the whole site.
 */

export interface PersonaLink {
  label: string;
  href: string;
  desc: string;
}

export interface PersonaLinkSet {
  heading: string;
  links: PersonaLink[];
}

const IETF_DRAFT =
  "https://datatracker.ietf.org/doc/draft-condrey-rats-pop-protocol/";
const GITHUB = "https://github.com/writerslogic";
const WRITERSPROOF = "https://writersproof.com";

export const PERSONA_LINKS: Record<ResolvedPersona, PersonaLinkSet> = {
  unknown: {
    heading: "Where to start",
    links: [
      {
        label: "WritersProof",
        href: WRITERSPROOF,
        desc: "Prove you wrote it, as you write it.",
      },
      {
        label: "Manifesto",
        href: "/manifesto",
        desc: "Why authorship proof matters now.",
      },
      ...(BLOG_ENABLED
        ? [
            {
              label: "Blog",
              href: "/blog",
              desc: "Field notes on provenance and proof.",
            },
          ]
        : []),
      {
        label: "Pricing",
        href: "/pricing",
        desc: "Free to start; plans as you grow.",
      },
    ],
  },
  writer: {
    heading: "For writers",
    links: [
      {
        label: "Get WritersProof",
        href: WRITERSPROOF,
        desc: "Prove you wrote it, as you write it. Free.",
      },
      {
        label: "Sign Your Text",
        href: "/sign",
        desc: "Sign it once, and it stays yours wherever it goes. Free.",
      },
      {
        label: "Pricing",
        href: "/pricing",
        desc: "Free to start; plans as you grow.",
      },
      {
        label: "Manifesto",
        href: "/manifesto",
        desc: "Why this matters for writers.",
      },
    ],
  },
  creator: {
    heading: "For creators",
    links: [
      {
        label: "Sign Your Text",
        href: "/sign",
        desc: "Sign it once, and it stays yours wherever it goes. Free.",
      },
      {
        label: "Invisible Watermark",
        href: "/zwc-watermark",
        desc: "How your name hides inside the text itself.",
      },
      {
        label: "Scrivener MCP",
        href: "/scrivener-mcp",
        desc: "Bring AI into your drafts on your terms.",
      },
      {
        label: "WritersProof",
        href: WRITERSPROOF,
        desc: "Prove you wrote it, as you write it.",
      },
    ],
  },
  developer: {
    heading: "Build on it",
    links: [
      {
        label: "Protocol Specification",
        href: "/protocol",
        desc: "Wire format, COSE/CBOR, and the IETF draft.",
      },
      {
        label: "SDK & API",
        href: "/developers",
        desc: "Libraries and integration surface.",
      },
      {
        label: "Security Model",
        href: "/security-model",
        desc: "Threat model and cryptographic guarantees.",
      },
      {
        label: "GitHub",
        href: GITHUB,
        desc: "Engine, protocol library, and tools — all open.",
      },
    ],
  },
  standards: {
    heading: "For the standards community",
    links: [
      {
        label: "Proof-of-Process Draft",
        href: IETF_DRAFT,
        desc: "Our RATS attestation Internet-Draft.",
      },
      {
        label: "Soft Bindings",
        href: "/soft-binding/overview",
        desc: "Durable content binding that survives stripping.",
      },
      {
        label: "Protocol Specification",
        href: "/protocol",
        desc: "How the pieces map to C2PA, COSE, and CBOR.",
      },
      {
        label: "Compliance",
        href: "/compliance",
        desc: "Where this fits emerging disclosure regimes.",
      },
    ],
  },
  publishing: {
    heading: "For publishing teams",
    links: [
      {
        label: "Partnerships",
        href: "/partnerships",
        desc: "Integrate authorship proof into your workflow.",
      },
      {
        label: "Executive Summary",
        href: "/executive-summary",
        desc: "The one-page case, for decision makers.",
      },
      {
        label: "Compliance",
        href: "/compliance",
        desc: "Provenance for AI-disclosure requirements.",
      },
      {
        label: "Licensing",
        href: "/licensing",
        desc: "Commercial terms for the engine and protocol.",
      },
    ],
  },
};

export function linksFor(persona: ResolvedPersona): PersonaLinkSet {
  return PERSONA_LINKS[persona] ?? PERSONA_LINKS.unknown;
}

export interface HeaderCTA {
  label: string;
  href: string;
}

/**
 * Persona-appropriate primary header CTA, or null to keep the default "Download Free".
 * Buyers (writer/creator) keep the product CTA; audiences who don't want the app get a
 * CTA that actually fits them.
 */
export function headerCtaFor(persona: ResolvedPersona): HeaderCTA | null {
  switch (persona) {
    case "developer":
      return { label: "Read the Protocol", href: "/protocol" };
    case "standards":
      return { label: "Read the IETF Draft", href: IETF_DRAFT };
    case "publishing":
      return { label: "Partner With Us", href: "/partnerships" };
    default:
      return null;
  }
}
